import React from "react";

import { Box, Typography } from "@mui/material";

import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/shared/constants";
import { useCurrentOrganization } from "@/modules/organization/useCurrentOrganization";

import {
  HOPE_FOREST_DEEP,
  HOPE_GREEN,
  HOPE_GREEN_LIGHT,
  HOPE_SERIF,
} from "./thehopeonebrand";

type HopeLogoProps = {
  size?: "sm" | "md" | "lg";

  showSparkle?: boolean;
};

const sizes = {
  sm: {
    mark: 24,

    fontSize: {
      xs: "1.2rem",
      sm: "1.25rem",
    },

    letterSpacing: {
      xs: "-0.02em",
      sm: "-0.025em",
    },

    gap: {
      xs: 0.75,
      sm: 0.9,
    },
  },

  md: {
    mark: 30,

    fontSize: {
      xs: "1.5rem",
      sm: "1.6rem",
    },

    letterSpacing: {
      xs: "-0.025em",
      sm: "-0.03em",
    },

    gap: {
      xs: 0.9,
      sm: 1.1,
    },
  },

  lg: {
    mark: 36,

    fontSize: {
      xs: "1.8rem",
      sm: "1.95rem",
    },

    letterSpacing: {
      xs: "-0.03em",
      sm: "-0.035em",
    },

    gap: {
      xs: 1,
      sm: 1.25,
    },
  },
};

/* -------------------------------------------------------------------------- */
/* Organic brand mark                                                         */
/* -------------------------------------------------------------------------- */

function HopeMark({
  size,
}: {
  size: number;
}) {
  return (
    <Box
      sx={{
        width: size,
        height: size,

        borderRadius: "50%",

        display: "grid",
        placeItems: "center",

        position: "relative",

        flexShrink: 0,

        bgcolor:
          "light-dark(#F4F4F4, #1A1A1A)",

        border:
          "1px solid light-dark(rgba(17,17,17,.08), rgba(255,255,255,.12))",

        boxShadow:
          "light-dark(0 4px 12px rgba(0,0,0,.08), 0 4px 14px rgba(0,0,0,.16))",
      }}
    >
      {/* Main leaf */}

      <Box
        sx={{
          width: size * 0.34,

          height: size * 0.5,

          bgcolor: HOPE_GREEN,

          borderRadius:
            "80% 10% 80% 10%",

          transform:
            "rotate(34deg)",

          position: "relative",

          "&::after": {
            content: '""',

            position: "absolute",

            left: "50%",

            top: "16%",

            width: "1px",

            height: "68%",

            transform:
              "translateX(-50%) rotate(-7deg)",

            bgcolor:
              HOPE_FOREST_DEEP,

            opacity: 0.45,

            borderRadius:
              "999px",
          },
        }}
      />

      {/* Secondary leaf */}

      <Box
        sx={{
          position: "absolute",

          width: size * 0.16,

          height: size * 0.25,

          bgcolor:
            HOPE_GREEN_LIGHT,

          borderRadius:
            "80% 10% 80% 10%",

          transform:
            "rotate(-36deg)",

          left: "25%",

          bottom: "22%",

          opacity: 0.95,
        }}
      />
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/* thehope.one logo                                                           */
/* -------------------------------------------------------------------------- */

export const HopeLogo: React.FC<
  HopeLogoProps
> = ({
  size = "md",

  showSparkle = false,
}) => {
  const s = sizes[size];

  const navigate =
    useNavigate();
  const { isOrganizationWorkspace } = useCurrentOrganization();

  return (
    <Box
      onClick={() =>
        navigate(isOrganizationWorkspace ? ROUTES.ORGANIZATION_HALL : "/TNPage")
      }
      sx={{
        display: "flex",

        alignItems: "center",

        gap: s.gap,

        cursor: "pointer",

        userSelect: "none",

        width: "fit-content",

        transition:
          "opacity 180ms ease, transform 180ms ease",

        "&:hover": {
          opacity: 0.88,

          transform:
            "translateY(-1px)",
        },

        "&:active": {
          transform:
            "scale(0.98)",
        },
      }}
    >
      {/* --------------------------------------------------------------- */}
      {/* Brand mark                                                       */}
      {/* --------------------------------------------------------------- */}

      <HopeMark
        size={s.mark}
      />

      {/* --------------------------------------------------------------- */}
      {/* Wordmark                                                         */}
      {/* --------------------------------------------------------------- */}

      <Typography
        component="span"
        sx={{
          display:
            "inline-flex",

          alignItems:
            "baseline",

          fontFamily:
            HOPE_SERIF,

          fontSize:
            s.fontSize,

          fontWeight: 700,

          letterSpacing:
            s.letterSpacing,

          lineHeight: 1,

          whiteSpace:
            "nowrap",

          color:
            "var(--text)",
        }}
      >
        thehope

        <Box
          component="span"
          sx={{
            ml: 0.05,

            color:
              "var(--text)",

            fontStyle:
              "italic",

            fontWeight: 700,
          }}
        >
          .one
        </Box>
      </Typography>
    </Box>
  );
};

export default HopeLogo;